import React, { useState, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Image,
  Icon,
  Container,
  Card,
  Wrap,
  WrapItem,
  Stack,
  Link,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import ProfilePreviewDisclaimer from './ProfilePreviewDisclaimer';
import ShareProfileButton from './ShareProfileButton.jsx';
import { getCofradiaPlaceholderIcon } from '../utils/cofradiaPlaceholderIcon';
import { resolveUploadsUrl } from '../utils/staticUrl.js';
import { formatFiestaLocalRango } from '../utils/fiestaLocalDisplay';
import {
  FaChurch,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaRegStar,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTiktok,
  FaPlay,
  FaGavel,
  FaHistory,
  FaShieldAlt,
  FaMusic,
  FaCamera,
  FaComments,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const normalizeWebsiteHref = (raw) => {
  if (!raw || !String(raw).trim()) return '#';
  const t = String(raw).trim();
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  return `https://${t.replace(/^\/+/, '')}`;
};

const PublicProfilePreviewCofradia = ({ isOpen, onClose, profileData, shareUrl = '' }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const fiestaRowBgPreview = useColorModeValue('purple.50', 'whiteAlpha.100');

  // Estado para el modal de imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Función para obtener el estilo del fondo de la card
  const getCardBackgroundStyle = (imagenFondo) => {
    if (imagenFondo && imagenFondo.trim() !== '') {
      return {
        backgroundImage: `url(${imagenFondo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return {
      background: 'linear-gradient(135deg, #805AD5 0%, #B794F6 50%, #F6AD55 100%)'
    };
  };

  // Función para generar URLs de redes sociales
  const getSocialUrl = (platform, value) => {
    if (!value) return null;
    
    // Si ya es una URL completa, devolverla
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    
    // Limpiar @ del inicio si existe
    let cleanValue = value.startsWith('@') ? value.substring(1) : value;
    
    // Limpiar prefijos de dominio si el usuario los puso sin protocolo (ej: www.facebook.com/usuario)
    cleanValue = cleanValue.replace(/^(www\.)?(instagram\.com|facebook\.com|youtube\.com|youtu\.be|tiktok\.com|x\.com|twitter\.com)\/?/i, '');
    
    switch (platform) {
      case 'instagram':
        return `https://instagram.com/${cleanValue}`;
      case 'facebook':
        return `https://facebook.com/${cleanValue}`;
      case 'youtube':
        // Si tiene @ al principio, es un handle de canal
        if (value.startsWith('@')) {
          return `https://youtube.com/@${cleanValue}`;
        }
        // Si parece una ruta de canal
        if (cleanValue.startsWith('c/') || cleanValue.startsWith('channel/') || cleanValue.startsWith('user/')) {
          return `https://youtube.com/${cleanValue}`;
        }
        // Si es solo un nombre, asumir que es un handle de canal
        return `https://youtube.com/@${cleanValue}`;
      case 'twitter':
        return `https://x.com/${cleanValue}`;
      case 'tiktok':
        return `https://tiktok.com/@${cleanValue}`;
      default:
        return null;
    }
  };

  // Función para extraer el ID de un video de YouTube
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };
  const isYouTubeShort = (url) => !!(url && /youtube\.com\/shorts\//.test(url));

  // Datos de la cofradía (sin datos mockeados)
  const variante =
    profileData?.perfilVariante ||
    profileData?.tipoEntidad?.variantePerfil ||
    'RELIGIOSO';

  const cofradiaData = {
    nombre: profileData?.nombre || '',
    tipo: profileData?.tipoEntidad?.nombre || profileData?.tipoGrupo || '',
    perfilVariante: variante,
    descripcion: profileData?.bio || '',
    localidad: profileData?.localizacion?.ciudad || '',
    provincia: profileData?.localizacion?.provincia || '',
    numHermanos: profileData?.numeroHermanos || '',
    fechaFundacion: profileData?.fechaFundacion || '',
    patrono: profileData?.patrono || '',
    sedeCanonica: profileData?.sedeCanonica || '',
    avatar: profileData?.avatar || null,
    galeria: profileData?.fotos || [],
    videos: profileData?.videos || [],
    contacto: {
      telefono: profileData?.contactoPublico?.telefono || '',
      email: profileData?.contactoPublico?.email || '',
      web: profileData?.contactoPublico?.website || '',
    },
    redes: {
      instagram: profileData?.redesSociales?.instagram || '',
      facebook: profileData?.redesSociales?.facebook || '',
      youtube: profileData?.redesSociales?.youtube || '',
      twitter: profileData?.redesSociales?.twitter || '',
      tiktok: profileData?.redesSociales?.tiktok || '',
    },
    especialidades: profileData?.actividadesYCultos || [],
    procesionesAnuales: profileData?.procesionesAnuales || '',
    horariosCulto: profileData?.horariosCulto || '',
    patrimonioArtistico: profileData?.patrimonioArtistico || '',
    confianza: {
      bandas: profileData?.bandasConfianza || 0,
      listaBandas: profileData?.listaBandasConfianza || []
    },
    habitantesMunicipio: profileData?.habitantesMunicipio,
    fiestasLocales: profileData?.fiestasLocales || [],
    patronDelPueblo: profileData?.patronDelPueblo || '',
    patronaDelPueblo: profileData?.patronaDelPueblo || '',
    culturalAmbito: profileData?.culturalAmbito || '',
    culturalNumeroSocios: profileData?.culturalNumeroSocios,
    culturalActividadesDescripcion: profileData?.culturalActividadesDescripcion || '',
  };

  const webContact =
    (cofradiaData.contacto.web && String(cofradiaData.contacto.web).trim()) || '';
  const hasSocialPreview = ['instagram', 'facebook', 'youtube', 'twitter', 'tiktok'].some(
    (k) => cofradiaData.redes[k] && String(cofradiaData.redes[k]).trim(),
  );
  const showContactoCard = !!(webContact || hasSocialPreview);

  // Estado para scroll infinito de bandas
  const [bandasVisibles, setBandasVisibles] = useState(3);
  const [cargandoMas, setCargandoMas] = useState(false);

  const cargarMasBandas = useCallback(() => {
    if (cargandoMas || bandasVisibles >= cofradiaData.confianza.listaBandas.length) return;
    
    setCargandoMas(true);
    setTimeout(() => {
      setBandasVisibles(prev => Math.min(prev + 3, cofradiaData.confianza.listaBandas.length));
      setCargandoMas(false);
    }, 800); // Simula carga
  }, [cargandoMas, bandasVisibles, cofradiaData.confianza.listaBandas.length]);

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header>
              <HStack>
                <Icon as={getCofradiaPlaceholderIcon(cofradiaData.perfilVariante)} color="purple.600" />
                <Text>Vista Previa del Perfil Público</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body p={0}>
              <Box bg={useColorModeValue('gray.50', 'gray.900')} minH="full">
                <Container maxW="4xl" py={6}>
                  <VStack gap={6} align="stretch">
                    
                    {/* Disclaimer */}
                    <ProfilePreviewDisclaimer profileType="cofradia" />
                    
                    {/* Header estilo LinkedIn/Facebook */}
                    <Box position="relative" w="full">
                      {/* Imagen de fondo */}
                      <Box
                        w="full"
                        h="320px"
                        rounded="lg"
                        overflow="hidden"
                        style={getCardBackgroundStyle(profileData?.fondoHeaderImagen)}
                        position="relative"
                      >
                        {/* Overlay sutil para mejor contraste */}
                        <Box
                          position="absolute"
                          top="0"
                          left="0"
                          right="0"
                          bottom="0"
                          bg="blackAlpha.200"
                        />
                      </Box>

                      {/* Caja blanca con la información */}
                      <Card.Root
                        mt="-20px"
                        rounded="2xl"
                        pt={6}
                        pb={6}
                        px={6}
                        bg={useColorModeValue('white', 'gray.800')}
                        shadow="xl"
                        border="1px solid"
                        borderColor={useColorModeValue('gray.200', 'gray.600')}
                        position="relative"
                        zIndex="1"
                      >
                        <VStack gap={4} align="center" textAlign="center">
                          {/* Información Principal */}
                          <VStack gap={2}>
                            <HStack gap={4} align="center">
                              {/* Avatar a la izquierda del nombre */}
                              <Avatar.Root
                                size="xl"
                                border="4px solid white"
                                bg="purple.100"
                                shadow="lg"
                                cursor={cofradiaData.avatar ? "pointer" : "default"}
                                onClick={() => cofradiaData.avatar && handleImageClick(resolveUploadsUrl(cofradiaData.avatar))}
                                _hover={cofradiaData.avatar ? { transform: 'scale(1.05)', transition: 'transform 0.2s' } : {}}><Avatar.Fallback name={cofradiaData.nombre}>{!cofradiaData.avatar ? (
                                    <Icon
                                      as={getCofradiaPlaceholderIcon(cofradiaData.perfilVariante)}
                                      boxSize={10}
                                    />
                                  ) : undefined}</Avatar.Fallback><Avatar.Image src={resolveUploadsUrl(cofradiaData.avatar)} /></Avatar.Root>
                              <VStack gap={1} align="start">
                                <Heading size="xl" color={useColorModeValue('gray.800', 'white')}>
                                  {cofradiaData.nombre}
                                </Heading>
                                <Text fontSize="lg" fontWeight="medium" color={useColorModeValue('gray.600', 'gray.300')}>
                                  {cofradiaData.tipo}
                                </Text>
                              </VStack>
                            </HStack>
                            <HStack gap={6} color={useColorModeValue('gray.500', 'gray.400')} flexWrap="wrap" justify="center">
                              <HStack gap={1}>
                                <Icon as={FaMapMarkerAlt} />
                                <Text fontSize="sm">{cofradiaData.localidad}, {cofradiaData.provincia}</Text>
                              </HStack>
                              {cofradiaData.perfilVariante === 'RELIGIOSO' && cofradiaData.numHermanos && (
                                <HStack gap={1}>
                                  <Icon as={FaUsers} />
                                  <Text fontSize="sm">{cofradiaData.numHermanos} hermanos</Text>
                                </HStack>
                              )}
                              {cofradiaData.perfilVariante === 'RELIGIOSO' && cofradiaData.fechaFundacion && (
                                <HStack gap={1}>
                                  <Icon as={FaCalendarAlt} />
                                  <Text fontSize="sm">Fundada en {cofradiaData.fechaFundacion}</Text>
                                </HStack>
                              )}
                              {cofradiaData.perfilVariante === 'AYUNTAMIENTO' &&
                                cofradiaData.habitantesMunicipio != null &&
                                cofradiaData.habitantesMunicipio !== '' && (
                                  <HStack gap={1}>
                                    <Icon as={FaUsers} />
                                    <Text fontSize="sm">
                                      {cofradiaData.habitantesMunicipio} habitantes (aprox.)
                                    </Text>
                                  </HStack>
                                )}
                            </HStack>
                          </VStack>

                          {/* Confianza - Solo mostrar si hay bandas que confían */}
                          {cofradiaData.confianza.bandas > 0 && (
                            <HStack gap={2} justify="center">
                              <Icon as={FaShieldAlt} color="green.500" w={4} h={4} />
                              <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')} fontWeight="medium">
                                {cofradiaData.confianza.bandas} bandas confían en esta entidad
                              </Text>
                            </HStack>
                          )}

                          <HStack gap={3} pt={2} flexWrap="wrap" justify="center">
                            <ShareProfileButton shareUrl={shareUrl} />
                          </HStack>
                        </VStack>
                      </Card.Root>
                    </Box>

                    {/* Acerca de - Solo mostrar si hay descripción */}
                    {cofradiaData.descripcion && (
                      <Card.Root rounded="2xl">
                        <Card.Header>
                          <Heading size="md">Acerca de</Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <Text lineHeight="tall" color={textColor}>
                            {cofradiaData.descripcion}
                          </Text>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Información Religiosa - Solo perfil religioso */}
                    {cofradiaData.perfilVariante === 'RELIGIOSO' &&
                    (cofradiaData.patrono || cofradiaData.sedeCanonica || cofradiaData.procesionesAnuales || cofradiaData.horariosCulto) && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaGavel} mr={2} color="purple.600" />
                            Información religiosa
                          </Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                            <VStack align="stretch" gap={3}>
                              {cofradiaData.patrono && (
                                <HStack>
                                  <Icon as={FaChurch} color="purple.500" />
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="medium">Patrono</Text>
                                    <Text fontSize="sm">{cofradiaData.patrono}</Text>
                                  </VStack>
                                </HStack>
                              )}
                              {cofradiaData.sedeCanonica && (
                                <HStack>
                                  <Icon as={FaCalendarAlt} color="purple.500" />
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="medium">Sede Canónica</Text>
                                    <Text fontSize="sm">{cofradiaData.sedeCanonica}</Text>
                                  </VStack>
                                </HStack>
                              )}
                              {cofradiaData.procesionesAnuales && (
                                <HStack>
                                  <Icon as={FaUsers} color="purple.500" />
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="medium">Procesiones Anuales</Text>
                                    <Text fontSize="sm">{cofradiaData.procesionesAnuales}</Text>
                                  </VStack>
                                </HStack>
                              )}
                            </VStack>
                            
                            {cofradiaData.horariosCulto && (
                              <VStack align="stretch" gap={3}>
                                <Text fontSize="sm" fontWeight="medium">Horarios de Culto</Text>
                                <Text fontSize="sm" color={textColor}>
                                  {cofradiaData.horariosCulto}
                                </Text>
                              </VStack>
                            )}
                          </SimpleGrid>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Patrimonio Artístico - Solo perfil religioso */}
                    {cofradiaData.perfilVariante === 'RELIGIOSO' && cofradiaData.patrimonioArtistico && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaHistory} mr={2} color="purple.600" />
                            Patrimonio artístico
                          </Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <Text lineHeight="tall" color={textColor}>
                            {cofradiaData.patrimonioArtistico}
                          </Text>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Municipio (ayuntamiento) */}
                    {cofradiaData.perfilVariante === 'AYUNTAMIENTO' &&
                      (cofradiaData.patronDelPueblo ||
                        cofradiaData.patronaDelPueblo ||
                        (cofradiaData.fiestasLocales && cofradiaData.fiestasLocales.length > 0)) && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaMapMarkerAlt} mr={2} color="purple.600" />
                            Municipio y fiestas
                          </Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack align="stretch" gap={3}>
                            {(cofradiaData.patronDelPueblo || cofradiaData.patronaDelPueblo) && (
                              <Text fontSize="sm" color={textColor}>
                                <Text as="span" fontWeight="semibold">
                                  Patronazgo:{' '}
                                </Text>
                                {[cofradiaData.patronDelPueblo, cofradiaData.patronaDelPueblo]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Text>
                            )}
                            {cofradiaData.fiestasLocales?.length > 0 && (
                              <VStack align="stretch" gap={2} w="full">
                                {cofradiaData.fiestasLocales.map((f, i) => {
                                  const rango = formatFiestaLocalRango(f);
                                  return (
                                    <HStack
                                      key={i}
                                      align="start"
                                      gap={3}
                                      p={3}
                                      rounded="lg"
                                      bg={fiestaRowBgPreview}
                                      borderWidth="1px"
                                      borderColor={borderColor}
                                    >
                                      <Icon as={FaCalendarAlt} color="purple.500" mt={0.5} flexShrink={0} />
                                      <Box minW={0}>
                                        <Text fontWeight="semibold" fontSize="sm">
                                          {f.nombre || 'Fiesta'}
                                        </Text>
                                        {rango ? (
                                          <Text fontSize="xs" color={mutedColor} mt={0.5}>
                                            {rango}
                                          </Text>
                                        ) : null}
                                        {f.notas ? (
                                          <Text fontSize="xs" color={textColor} mt={1} lineClamp={3}>
                                            {f.notas}
                                          </Text>
                                        ) : null}
                                      </Box>
                                    </HStack>
                                  );
                                })}
                              </VStack>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Asociación cultural */}
                    {cofradiaData.perfilVariante === 'ASOCIACION_CULTURAL' &&
                      (cofradiaData.culturalAmbito ||
                        cofradiaData.culturalNumeroSocios ||
                        cofradiaData.culturalActividadesDescripcion) && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaMusic} mr={2} color="purple.600" />
                            Asociación cultural
                          </Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack align="stretch" gap={2}>
                            {cofradiaData.culturalAmbito && (
                              <Text fontSize="sm" color={textColor}>
                                <Text as="span" fontWeight="semibold">
                                  Ámbito:{' '}
                                </Text>
                                {cofradiaData.culturalAmbito}
                              </Text>
                            )}
                            {cofradiaData.culturalNumeroSocios != null && cofradiaData.culturalNumeroSocios !== '' && (
                              <Text fontSize="sm" color={textColor}>
                                Socios (aprox.): {cofradiaData.culturalNumeroSocios}
                              </Text>
                            )}
                            {cofradiaData.culturalActividadesDescripcion && (
                              <Text lineHeight="tall" color={textColor}>
                                {cofradiaData.culturalActividadesDescripcion}
                              </Text>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Especialidades - Solo mostrar si hay actividades */}
                    {cofradiaData.perfilVariante === 'RELIGIOSO' &&
                    cofradiaData.especialidades &&
                    cofradiaData.especialidades.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">Actividades y cultos</Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <Wrap gap={2}>
                            {cofradiaData.especialidades.map((especialidad, index) => (
                              <WrapItem key={index}>
                                <Badge 
                                  colorPalette="purple" 
                                  variant="subtle" 
                                  px={3} 
                                  py={1}
                                  rounded="full"
                                >
                                  {especialidad}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Información de Contacto (misma lógica que perfil público: web y/o redes) */}
                    {showContactoCard && (
                    <Card.Root>
                      <Card.Header>
                        <Heading size="md">Información de contacto</Heading>
                      </Card.Header>
                      <Card.Body pt={0}>
                        <VStack gap={6} align="center" w="full">
                          {webContact && (
                            <Box w="full" maxW="md">
                              <Link
                                href={normalizeWebsiteHref(webContact)}
                                color="purple.600"
                                _hover={{ textDecoration: 'none', opacity: 0.85 }}
                                target='_blank'
                                rel='noopener noreferrer'>
                                <HStack justify="center" gap={2} flexWrap="wrap">
                                  <Icon as={FaGlobe} color="purple.500" flexShrink={0} />
                                  <Text fontSize="sm" fontWeight="medium" textAlign="center" wordBreak="break-word">
                                    {webContact}
                                  </Text>
                                </HStack>
                              </Link>
                            </Box>
                          )}

                          {webContact && hasSocialPreview && <Separator w="full" maxW="md" />}

                          {hasSocialPreview && (
                            <VStack align="stretch" gap={3} w="full" maxW="md">
                              <Text fontSize="sm" fontWeight="600" w="full" textAlign="center">
                                Síguenos en redes sociales
                              </Text>
                              <HStack justify="center" gap={4} flexWrap="wrap">
                                {cofradiaData.redes.instagram && (
                                <Link
                                  href={getSocialUrl('instagram', cofradiaData.redes.instagram)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaInstagram} color="pink.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {cofradiaData.redes.instagram}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {cofradiaData.redes.facebook && (
                                <Link
                                  href={getSocialUrl('facebook', cofradiaData.redes.facebook)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaFacebook} color="blue.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {cofradiaData.redes.facebook}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {cofradiaData.redes.youtube && (
                                <Link
                                  href={getSocialUrl('youtube', cofradiaData.redes.youtube)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaYoutube} color="red.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {cofradiaData.redes.youtube}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {cofradiaData.redes.twitter && (
                                <Link
                                  href={getSocialUrl('twitter', cofradiaData.redes.twitter)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaXTwitter} color="gray.800" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {cofradiaData.redes.twitter}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {cofradiaData.redes.tiktok && (
                                <Link
                                  href={getSocialUrl('tiktok', cofradiaData.redes.tiktok)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaTiktok} color="gray.800" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {cofradiaData.redes.tiktok}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              </HStack>
                            </VStack>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                    )}

                    {/* Fotos y Vídeos - Solo mostrar si hay fotos o vídeos */}
                    {((cofradiaData.galeria && cofradiaData.galeria.length > 0) || 
                      (cofradiaData.videos && cofradiaData.videos.length > 0)) && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">Fotos y Vídeos</Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={6} align="stretch">
                            {/* Fotos */}
                            {cofradiaData.galeria && cofradiaData.galeria.length > 0 && (
                              <Box>
                                <Text fontSize="md" fontWeight="medium" mb={3}>Galería de fotos</Text>
                                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                                  {cofradiaData.galeria.map((foto, index) => {
                                    const fotoUrl = typeof foto === 'string' ? foto : foto.url;
                                    return (
                                      <Image
                                        key={index}
                                        src={fotoUrl}
                                        alt={`Foto ${index + 1}`}
                                        rounded="lg"
                                        objectFit="cover"
                                        h="120px"
                                        w="full"
                                        cursor="pointer"
                                        onClick={() => handleImageClick(fotoUrl)}
                                        _hover={{ 
                                          transform: 'scale(1.05)',
                                          shadow: 'lg'
                                        }}
                                        transition="all 0.2s" />
                                    );
                                  })}
                                </SimpleGrid>
                              </Box>
                            )}

                          {/* Vídeos de YouTube */}
                          {cofradiaData.videos && cofradiaData.videos.length > 0 && (
                            <>
                              <Separator />
                              <Box>
                                <HStack mb={3} justify="space-between" align="center">
                                  <Text fontSize="md" fontWeight="medium">
                                    <Icon as={FaYoutube} mr={2} color="red.500" />
                                    Vídeos
                                  </Text>
                                  <Badge colorPalette="red" fontSize="xs">
                                    {cofradiaData.videos.length} videos
                                  </Badge>
                                </HStack>
                                {(() => {
                                  const normalVideos = cofradiaData.videos.filter(v => !isYouTubeShort(v.url));
                                  const shorts       = cofradiaData.videos.filter(v =>  isYouTubeShort(v.url));
                                  const renderCard = (video, index) => {
                                    const videoId = extractYouTubeId(video.url);
                                    const short = isYouTubeShort(video.url);
                                    return (
                                      <Box key={index} position="relative" rounded="lg" overflow="hidden" shadow="md" _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }} transition="all 0.3s">
                                        {videoId ? (
                                          <Box position="relative" pb={short ? '177.78%' : '56.25%'}>
                                            <Box
                                              as="iframe"
                                              src={`https://www.youtube.com/embed/${videoId}`}
                                              title={video.titulo}
                                              position="absolute"
                                              top={0} left={0} w="full" h="full"
                                              frameBorder="0"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                            />
                                            {short && (
                                              <Badge position="absolute" top={2} left={2} colorPalette="red" fontSize="2xs" rounded="md" px={1.5} zIndex={1} pointerEvents="none">Short</Badge>
                                            )}
                                          </Box>
                                        ) : (
                                          <Box w="full" h="180px" bg="gray.200" display="flex" alignItems="center" justifyContent="center">
                                            <VStack>
                                              <Icon as={FaYoutube} w={10} h={10} color="gray.400" />
                                              <Text fontSize="xs" color="gray.500">Video no disponible</Text>
                                            </VStack>
                                          </Box>
                                        )}
                                        <Box p={3} bg={cardBg}>
                                          <Text fontSize="sm" fontWeight="medium" lineClamp={2} color={textColor}>{video.titulo}</Text>
                                        </Box>
                                      </Box>
                                    );
                                  };
                                  return (
                                    <VStack gap={4} align="stretch">
                                      {normalVideos.length > 0 && (
                                        <Box>
                                          {shorts.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Vídeos</Text>}
                                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                            {normalVideos.map((v, i) => renderCard(v, i))}
                                          </SimpleGrid>
                                        </Box>
                                      )}
                                      {shorts.length > 0 && (
                                        <Box>
                                          {normalVideos.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Shorts</Text>}
                                          <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                                            {shorts.map((v, i) => renderCard(v, i))}
                                          </SimpleGrid>
                                        </Box>
                                      )}
                                    </VStack>
                                  );
                                })()}
                              </Box>
                            </>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Bandas que Confían - Solo mostrar si hay bandas */}
                    {cofradiaData.confianza.listaBandas && cofradiaData.confianza.listaBandas.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaShieldAlt} mr={2} color="green.500" />
                            Bandas que Confían en Nosotros
                          </Heading>
                          <Text fontSize="sm" color={textColor}>
                            Agrupaciones musicales que han colaborado con nuestra entidad
                          </Text>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={4} align="stretch">
                            {cofradiaData.confianza.listaBandas.slice(0, bandasVisibles).map((banda) => (
                              <Card.Root key={banda.id} variant="outline" bg={useColorModeValue('white', 'gray.700')}>
                                <Card.Body p={4}>
                                  <HStack gap={4} align="center">
                                    {/* Logo de la banda */}
                                    <Avatar.Root size="md" bg="purple.100" color="purple.600"><Avatar.Fallback name={banda.nombre}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={banda.logo} /></Avatar.Root>
                                    
                                    {/* Información de la banda */}
                                    <VStack align="start" gap={1} flex={1}>
                                      <Heading size="sm" color={useColorModeValue('gray.800', 'white')}>
                                        {banda.nombre}
                                      </Heading>
                                      <HStack gap={2}>
                                        <Badge colorPalette="blue" size="sm">
                                          {banda.tipo}
                                        </Badge>
                                        <Text fontSize="xs" color={textColor}>
                                          Colaboración el {formatearFecha(banda.fechaAcuerdo)}
                                        </Text>
                                      </HStack>
                                    </VStack>

                                    {/* Icono de validación */}
                                    <Icon as={FaShieldAlt} color="green.500" w={5} h={5} />
                                  </HStack>
                                </Card.Body>
                              </Card.Root>
                            ))}

                            {/* Botón para cargar más */}
                            {bandasVisibles < cofradiaData.confianza.listaBandas.length && (
                              <Box textAlign="center" pt={4}>
                                <Button
                                  variant="ghost"
                                  colorPalette="purple"
                                  onClick={cargarMasBandas}
                                  loading={cargandoMas}
                                  loadingText="Cargando más..."
                                  size="sm"
                                >
                                  Ver más bandas ({cofradiaData.confianza.listaBandas.length - bandasVisibles} restantes)
                                </Button>
                              </Box>
                            )}

                            {/* Mensaje cuando se han mostrado todas */}
                            {bandasVisibles >= cofradiaData.confianza.listaBandas.length && cofradiaData.confianza.listaBandas.length > 3 && (
                              <Box textAlign="center" pt={2}>
                                <Text fontSize="sm" color={mutedColor} fontStyle="italic">
                                  Has visto todas las bandas que confían en esta entidad
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Sección de Reseñas */}
                    {profileData.resenas && profileData.resenas.filter(r => r.isPublished).length > 0 && (
                      <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full">
                        <Card.Header>
                          <HStack gap={3}>
                            <Icon as={FaComments} w={6} h={6} color="purple.500" />
                            <Heading size="md" color={useColorModeValue('gray.800', 'white')}>
                              Reseñas Recibidas
                            </Heading>
                            <Badge colorPalette="purple" fontSize="sm" px={3} py={1} rounded="full">
                              {profileData.resenas.filter(r => r.isPublished).length}
                            </Badge>
                          </HStack>
                        </Card.Header>
                        <Card.Body>
                          <VStack gap={4} align="stretch">
                            {profileData.resenas
                              .filter(r => r.isPublished)
                              .slice(0, 3)
                              .map((resena) => (
                                <Card.Root key={resena.id} variant="outline" bg="white">
                                  <Card.Body p={4}>
                                    <VStack gap={3} align="stretch">
                                      {/* Header de la reseña */}
                                      <HStack gap={3} justify="space-between">
                                        <HStack gap={3}>
                                          <Avatar.Root size="sm" bg="purple.100" color="purple.600"><Avatar.Fallback name={resena.bandaName}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={resena.bandaAvatar} /></Avatar.Root>
                                          <VStack align="start" gap={0}>
                                            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                              {resena.bandaName || 'Anónimo'}
                                            </Text>
                                            <Text fontSize="xs" color="gray.600">
                                              {resena.eventoNombre && `${resena.eventoNombre} • `}
                                              {new Date(resena.receivedDate).toLocaleDateString('es-ES', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                              })}
                                            </Text>
                                          </VStack>
                                        </HStack>
                                        
                                        {/* Rating */}
                                        <HStack gap={1}>
                                          {[...Array(5)].map((_, i) => (
                                            <Icon
                                              key={i}
                                              as={i < Math.round(resena.averageRating) ? FaStar : FaRegStar}
                                              w={4}
                                              h={4}
                                              color={i < Math.round(resena.averageRating) ? "yellow.500" : "gray.300"}
                                            />
                                          ))}
                                        </HStack>
                                      </HStack>
                                      
                                      {/* Comentario */}
                                      {resena.comment && (
                                        <Text fontSize="sm" color="gray.600" lineHeight="tall" fontStyle="italic">
                                          "{resena.comment}"
                                        </Text>
                                      )}
                                    </VStack>
                                  </Card.Body>
                                </Card.Root>
                              ))}
                            
                            {profileData.resenas.filter(r => r.isPublished).length > 3 && (
                              <Box textAlign="center" pt={2}>
                                <Text fontSize="sm" color={mutedColor} fontStyle="italic">
                                  Mostrando las 3 reseñas más recientes de {profileData.resenas.filter(r => r.isPublished).length} totales
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}
                  </VStack>
                </Container>
              </Box>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack gap={3}>
                <Text fontSize="sm" color={mutedColor}>
                  Así verán tu perfil las bandas
                </Text>
                <Button variant="ghost" onClick={onClose}>
                  Cerrar
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

        {/* Modal para ver imagen en grande */}
        <Dialog.Root open={isImageModalOpen} size='full' placement='center' onOpenChange={e => {
          if (!e.open) {
            handleCloseImageModal();
          }
        }}>
          <Portal>

            <Dialog.Backdrop bg="blackAlpha.800" />
            <Dialog.Positioner>
              <Dialog.Content bg="transparent" boxShadow="none">
                <Dialog.CloseTrigger
                  color="white"
                  size="lg"
                  bg="blackAlpha.600"
                  _hover={{ bg: 'blackAlpha.700' }}
                  rounded="full"
                  m={4}
                />
                <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4}>
                  {selectedImage && (
                    <Image
                      src={selectedImage}
                      alt="Imagen en tamaño completo"
                      maxH="90vh"
                      maxW="90vw"
                      objectFit="contain"
                    />
                  )}
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>

          </Portal>
        </Dialog.Root>

      </Portal>
    </Dialog.Root>
  );
};

export default PublicProfilePreviewCofradia;
